import { useMemo, useState } from 'react'
import { FileQuestion } from 'lucide-react'
import { Button, Modal, Select } from '@/components/ui'
import { TablePaginationBar } from '@/components/common/TablePaginationBar'
import { usePlaceFileInCompany, useUnplacedFiles } from '../../hooks/useDrive'
import { countLabel } from '../../utils/driveTree'

const PAGE_SIZE = 25

export interface UnplacedFilesNoticeProps {
  /** The companies this person can work in; files can only go to one of these. */
  companies: Array<{ _id: string; name: string }>
}

/**
 * Older employee files with no company recorded don't appear in any company's
 * list, so HR needs a way to find them and say where they belong.
 */
export function UnplacedFilesNotice({ companies }: UnplacedFilesNoticeProps): React.ReactElement | null {
  const [isOpen, setIsOpen] = useState(false)
  const [page, setPage] = useState(1)
  const { data } = useUnplacedFiles(page, PAGE_SIZE)
  const place = usePlaceFileInCompany()
  const reachable = useMemo(() => new Set(companies.map((company) => company._id)), [companies])

  const total = data?.pagination.total ?? 0
  if (total === 0) return null

  return (
    <>
      <div className="flex flex-col gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-500/30 dark:bg-amber-500/10 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-start gap-2.5">
          <FileQuestion className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
          <p className="text-sm text-gray-800 dark:text-gray-200">
            {countLabel(total, 'older employee file')} {total === 1 ? 'has' : 'have'} no company, so{' '}
            {total === 1 ? "it doesn't" : "they don't"} show in the lists below.
          </p>
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => setIsOpen(true)}
          className="shrink-0 self-start whitespace-nowrap sm:self-auto"
        >
          Place files
        </Button>
      </div>

      <Modal open={isOpen} onClose={() => setIsOpen(false)} title="Place older files" className="max-w-2xl">
        <div className="space-y-3">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Choose which of the employee&apos;s companies each file belongs to.
          </p>
          <ul className="divide-y divide-gray-100 dark:divide-gray-800" aria-label="Files to place">
            {(data?.data ?? []).map((file) => {
              const options = file.ownerCompanies
                .filter((company) => reachable.has(company.id))
                .map((company) => ({ value: company.id, label: company.name }))
              return (
                <li
                  key={file.id}
                  className="flex flex-col gap-2 py-2.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                      {file.originalName}
                    </p>
                    <p className="truncate text-xs text-gray-500 dark:text-gray-400">{file.owner.name}</p>
                  </div>
                  {options.length > 0 ? (
                    <Select
                      options={options}
                      value=""
                      placeholder="Choose company"
                      aria-label={`Company for ${file.originalName}`}
                      disabled={place.isPending}
                      onChange={(companyId) => {
                        if (companyId) place.mutate({ id: file.id, companyId })
                      }}
                      className="w-full shrink-0 sm:w-56"
                    />
                  ) : (
                    <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400">
                      Not in a company you can reach
                    </span>
                  )}
                </li>
              )
            })}
          </ul>
          {total > PAGE_SIZE ? (
            <TablePaginationBar
              page={page}
              limit={PAGE_SIZE}
              total={total}
              totalPages={Math.ceil(total / PAGE_SIZE)}
              onPageChange={setPage}
            />
          ) : null}
        </div>
      </Modal>
    </>
  )
}

UnplacedFilesNotice.displayName = 'UnplacedFilesNotice'
